import { useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Upload, X, Camera, Save } from "lucide-react";
import { useBoard, boardStore } from "@/lib/boardStore";
import ConnectionEditor from "@/components/panel/ConnectionEditor";
import { toast } from "sonner";

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Compress an image to a max dimension to keep localStorage small
const compressImage = (dataUrl, maxDim = 800, quality = 0.82) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

export default function SidePanel({
  open,
  onClose,
  editingCardId,
  setEditingCardId,
  onManageCategories,
}) {
  const { cards, categories } = useBoard();
  const editing = useMemo(
    () => cards.find((c) => c.id === editingCardId) || null,
    [cards, editingCardId]
  );

  const [text, setText] = useState("");
  const [caption, setCaption] = useState("");
  const [photo, setPhoto] = useState(null);
  const [categoryId, setCategoryId] = useState(categories[0]?.id || "");
  const fileRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setText(editing.text || "");
      setCaption(editing.caption || "");
      setPhoto(editing.photo || null);
      setCategoryId(editing.categoryId || categories[0]?.id || "");
    } else {
      setText("");
      setCaption("");
      setPhoto(null);
      setCategoryId(categories[0]?.id || "");
    }
  }, [editing, open, categories]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    try {
      const raw = await readAsBase64(file);
      const compressed = await compressImage(raw);
      setPhoto(compressed);
    } catch {
      toast.error("Failed to read image");
    }
  };

  const handleSave = () => {
    // Photo card is title-only — strip caption
    const payload = {
      text: text.trim(),
      caption: photo ? "" : caption.trim(),
      photo,
      categoryId,
    };
    if (!payload.text && !payload.photo) {
      toast.error("Add some text or a photo before saving");
      return;
    }
    if (editing) {
      boardStore.updateCard(editing.id, payload);
      toast("Node updated");
      onClose();
    } else {
      // Grid-cascade new cards so they don't stack on top of each other
      const count = boardStore.getState().cards.length;
      const cols = 3;
      const col = count % cols;
      const row = Math.floor(count / cols);
      const baseX = window.innerWidth / 2 - 380;
      const baseY = 140;
      boardStore.addCard({
        ...payload,
        x: baseX + col * 280 + (row % 2) * 40,
        y: baseY + row * 210,
      });
      toast("Node pinned to the board");
      onClose();
    }
  };

  const handleDelete = () => {
    if (!editing) return;
    if (!window.confirm("Remove this node and its connections?")) return;
    boardStore.removeCard(editing.id);
    toast("Node removed");
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        data-testid="side-panel"
        className="w-full sm:max-w-md bg-[#0f0b07] border-l border-[#5c4426]/40 text-[#f4ebd8] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-[#5c4426]/30">
          <SheetTitle className="noir-title text-2xl text-[#f4d9a8]">
            {editing ? "Edit Node" : "New Node"}
          </SheetTitle>
          <SheetDescription className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285]/60">
            Node entry
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto thin-scroll px-6 py-4 space-y-5">
          {/* Category */}
          <div className="space-y-2">
            <Label className="typewriter text-[#c9b285] text-xs uppercase tracking-widest">
              Category
            </Label>
            <div className="flex gap-2">
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger
                  data-testid="category-select"
                  className="bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8] typewriter"
                >
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8]">
                  {categories.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      className="typewriter focus:bg-[#5c4426]/40 focus:text-[#f4ebd8]"
                      data-testid={`category-option-${c.id}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full"
                          style={{ background: c.color }}
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={onManageCategories}
                data-testid="edit-categories-btn"
                className="border-[#5c4426]/50 bg-transparent text-[#f4d9a8] hover:bg-[#5c4426]/30 typewriter text-xs"
              >
                Manage
              </Button>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-2">
            <Label className="typewriter text-[#c9b285] text-xs uppercase tracking-widest">
              Name / Title
            </Label>
            <Input
              data-testid="card-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Vincent Corelli, 34 Elm St, Enron Corp..."
              className="bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8] typewriter placeholder:text-[#c9b285]/40"
              maxLength={80}
            />
          </div>

          {/* Caption — hidden for polaroids (photo cards get title only) */}
          {!photo && (
            <div className="space-y-2">
              <Label className="typewriter text-[#c9b285] text-xs uppercase tracking-widest">
                Note / Caption (optional)
              </Label>
              <Textarea
                data-testid="card-caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Handwritten scribble on the back..."
                className="bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8] handwritten text-lg placeholder:text-[#c9b285]/40 min-h-[70px]"
                maxLength={200}
              />
            </div>
          )}

          {/* Photo */}
          <div className="space-y-2">
            <Label className="typewriter text-[#c9b285] text-xs uppercase tracking-widest">
              Photo (optional)
            </Label>
            {photo ? (
              <div className="relative w-40 group">
                <div className="polaroid p-2">
                  <div className="polaroid-photo aspect-square overflow-hidden">
                    <img
                      src={photo}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="h-6" />
                </div>
                <button
                  data-testid="remove-photo-btn"
                  onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#8b1a1a] text-white flex items-center justify-center shadow"
                  title="Remove photo"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                data-testid="upload-photo-btn"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-[#5c4426]/50 rounded-md py-6 flex flex-col items-center gap-2 text-[#c9b285]/70 hover:bg-[#5c4426]/15 hover:text-[#f4d9a8] transition-colors"
              >
                <Camera size={22} />
                <span className="typewriter text-xs">
                  Upload from device
                </span>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="photo-file-input"
            />
          </div>

          {/* Connections */}
          {editing && (
            <div className="pt-2 border-t border-[#5c4426]/30">
              <ConnectionEditor cardId={editing.id} />
            </div>
          )}

          {!editing && (
            <div className="pt-2 border-t border-[#5c4426]/30 text-[#c9b285]/60 typewriter text-xs">
              Save the node first to add connections.
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[#5c4426]/30 px-6 py-4 flex items-center justify-between gap-2 bg-[#0b0805]">
          {editing ? (
            <Button
              variant="outline"
              onClick={handleDelete}
              data-testid="delete-card-btn"
              className="border-[#8b1a1a]/60 bg-transparent text-[#e08a8a] hover:bg-[#8b1a1a]/20 typewriter"
            >
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="cancel-btn"
              className="text-[#c9b285] hover:bg-[#5c4426]/30 typewriter"
            >
              Close
            </Button>
            <Button
              onClick={handleSave}
              data-testid="save-card-btn"
              className="bg-[#c9702a] hover:bg-[#e08432] text-[#1a140c] typewriter font-semibold"
            >
              <Save size={14} className="mr-1" />
              {editing ? "Save" : "Pin to Board"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
